import { useCachedData } from '../hooks/useCachedData';
import { api, endpoints } from '../api/config';
import { useQuery } from '@tanstack/react-query';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, TextField, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
} from '../constants/styles';

const DETAILS_COUNT_BADGE_SX = {
  minWidth: 96,
  width: 96,
  height: 26,
  borderRadius: '13px',
  fontSize: '0.75rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  flexShrink: 0,
};

// Helper function to group inventory items
function groupInventoryItems(data) {
  if (!data || !Array.isArray(data)) {
    return {
      items: [],
      uniqueLocations: []
    };
  }
  
  // Create a map to group items and track unique locations
  const groupedMap = new Map();
  const uniqueLocations = new Set();
  
  data.forEach(item => {
    // Create a unique key combining item name and internal_id
    const itemKey = `${item.item}_${item.internal_id}`; // Using combination of item and internal_id as key
    if (!groupedMap.has(itemKey)) {
      groupedMap.set(itemKey, {
        item: item.item,
        internal_id: item.internal_id || '',
        total_quantity: 0,
        available_quantity: 0,
        locations_count: new Set(),
        locations: new Set(),
        locationQuantities: {}, // New object to store quantities by location
        details: []
      });
    }
    
    const group = groupedMap.get(itemKey);
    
    // Parse quantities with proper error handling
    const parseQuantity = (value) => {
      if (value === null || value === undefined) return 0;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Get the available quantity from the item
    const available = parseQuantity(item.available_quantity) || 
                     parseQuantity(item.quantity_available) || 
                     parseQuantity(item.available) || 
                     parseQuantity(item.total_available) || 0;

    // Add to total quantity
    group.total_quantity += available;
    
    // Add to available quantity
    group.available_quantity += available;
    
    // Add location to unique locations count
    if (item.location_id) {
      group.locations_count.add(item.location_id);
    }
    
    // Add location name to unique locations set and track quantity
    if (item.location) {
      group.locations.add(item.location);
      uniqueLocations.add(item.location);
      
      // Initialize location quantity if not exists
      if (!group.locationQuantities[item.location]) {
        group.locationQuantities[item.location] = 0;
      }
      
      // Add quantity to location total
      group.locationQuantities[item.location] += available;
    }
    
    // Add the full item details to the group
    group.details.push(item);
  });
  
  // Convert the map to an array and format the data
  return {
    items: Array.from(groupedMap.values()).map(group => {
      // Verify that total quantity matches sum of location quantities
      const locationSum = Object.values(group.locationQuantities).reduce((sum, qty) => sum + qty, 0);
      
      // If there's a discrepancy, log it for debugging
      if (Math.abs(locationSum - group.total_quantity) > 0.01) {
        console.warn(`Quantity mismatch for item ${group.item}:`, {
          total: group.total_quantity,
          locationSum,
          difference: group.total_quantity - locationSum
        });
      }
      
      return {
        internal_id: group.internal_id,
        item: group.item,
        item_name: group.details[0]?.item_name || '-',
        item_id: group.details[0]?.item_id || group.details[0]?.itemid || group.details[0]?.id || group.details[0]?.itemId || '-',
        total_quantity: group.total_quantity,
        available_quantity: group.available_quantity,
        locations_count: group.locations_count.size,
        locations: Array.from(group.locations),
        details: group.details,
        ...group.locationQuantities // Spread location quantities as individual fields
      };
    }),
    uniqueLocations: Array.from(uniqueLocations)
  };
}

const Inventory = () => {
  const { data: locationsData } = useCachedData(endpoints.locations);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(() => {
    return localStorage.getItem('inventory-location') || '';
  });

  useEffect(() => {
    if (!locationsData) return;

    const list = Array.isArray(locationsData)
      ? locationsData
      : Object.values(locationsData);

    const uniqueLocations = list
      .map((loc) => loc.location || loc.name || loc.id)
      .filter(Boolean);

    setLocations(uniqueLocations);

    if (!location && uniqueLocations.length > 0) {
      const defaultLoc =
        uniqueLocations.find((loc) =>
          String(loc).toLowerCase().includes('flemington')
        ) || uniqueLocations[0] || '';
      setLocation(defaultLoc);
      if (defaultLoc) {
        localStorage.setItem('inventory-location', defaultLoc);
      }
    }
  }, [locationsData, location]);

  const inventoryQuery = useQuery({
    queryKey: ['data_inventory', location],
    queryFn: async () => {
      if (!location) return [];
      const response = await api.get(endpoints.inventory, {
        params: { location }
      });
      const raw = response.data?.data ?? response.data;
      return Array.isArray(raw) ? raw : raw ? [raw] : [];
    },
    enabled: Boolean(location),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,
  });

  const { data, isLoading, error, refetch } = inventoryQuery;
  
  // Main table column visibility
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inventory-column-visibility');
    return saved ? JSON.parse(saved) : {
      internal_id: true,
      item_id: true,
      item_name: true,
      total_quantity: true,
      details: true
    };
  });

  // Save main table column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('inventory-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Process and group the data
  const { items: processedData, uniqueLocations } = useMemo(() => {
    return groupInventoryItems(data);
  }, [data]);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');

  // Handle row click to open details dialog
  const handleRowClick = (row) => {
    setSelectedDetails(Array.isArray(row?.details) ? row.details : []);
    setSelectedItemName(row?.item_name || row?.item || 'Item');
    setDetailsDialogOpen(true);
  };

  const detailColumns = useMemo(() => {
    if (!selectedDetails.length) return [];
    const keys = Object.keys(selectedDetails[0]).filter((key) => !['item_id', 'itemid', 'id', 'itemId'].includes(key));
    return keys.map((key) => ({
      accessorKey: key,
      header:
        key === 'item_name'
          ? 'Item Name'
          : key
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        return typeof value === 'number' ? Math.round(value) : value;
      },
    }));
  }, [selectedDetails]);

  const inventoryDetailGrid = useDataGridProTable({
    data: selectedDetails,
    title: `Details for ${selectedItemName || 'Item'}`,
    columns: detailColumns,
    getRowId: (row, index) =>
      `${row?.internal_id || selectedItemName || 'row'}_${row?.location_id || ''}_${row?.location || ''}_${index}`,
    loading: false,
    error: null,
  });

  // Main table columns
  const columns = useMemo(() => {
    // Base columns
    const baseColumns = [
      {
        accessorKey: 'internal_id',
        header: 'Internal ID',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'item_id',
        header: 'Item ID',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'item_name',
        header: 'Item Name',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
      },
      {
        accessorKey: 'total_quantity',
        header: 'Total Quantity',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return typeof value === 'number' ? Math.round(value) : '0';
        }
      }
    ];

    // Add dynamic location columns
    if (Array.isArray(uniqueLocations)) {
      uniqueLocations.forEach(location => {
        baseColumns.push({
          accessorKey: location,
          header: location,
          minSize: COLUMN_MIN_SIZE,
          size: COLUMN_SIZE,
          cell: info => {
            const value = info.getValue();
            return typeof value === 'number' ? Math.round(value) : '0';
          }
        });
      });
    }

    // Add details count column at the end
    baseColumns.push({
      accessorKey: 'details',
      header: 'Details Count',
      minSize: 120,
      size: 150,
      cell: info => {
        const details = info.getValue();
        const count = Array.isArray(details) ? details.length : 0;

        return (
          <Box
            component="span"
            sx={{
              ...DETAILS_COUNT_BADGE_SX,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {count} details
          </Box>
        );
      },
    });

    return baseColumns;
  }, [uniqueLocations]);

  const inventoryMainGrid = useDataGridProTable({
    data: processedData,
    loading: isLoading,
    error: error ? error.message || 'Failed to load inventory' : null,
    title: 'Inventory',
    columns,
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    onRowClick: handleRowClick,
    actionButtons: [
      <Box key="inventory-location-filter" sx={{ minWidth: { xs: 180, sm: 220 } }}>
        <TextField
          select
          fullWidth
          label="Location"
          value={location}
          onChange={(e) => {
            const newLoc = e.target.value;
            setLocation(newLoc);
            localStorage.setItem('inventory-location', newLoc);
          }}
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              height: '36px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
            },
          }}
        >
          {locations.map((loc) => (
            <MenuItem key={loc} value={loc} sx={{ fontSize: '0.875rem' }}>
              {loc}
            </MenuItem>
          ))}
        </TextField>
      </Box>,
    ],
  });

  return (
    <DashboardLayout>
      <MuiXDataGridSurface hook={inventoryMainGrid} DataGridPro={DataGridPro} />
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Details for {selectedItemName || 'Item'}</Typography>
          <IconButton aria-label="close" onClick={() => setDetailsDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedDetails.length > 0 ? (
            <MuiXDataGridSurface hook={inventoryDetailGrid} DataGridPro={DataGridPro} />
          ) : (
            <Typography sx={{ p: 2 }}>No details available for this inventory item.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Inventory; 